/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Image,
  Switch,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';

const categories = [
  'Breaking News',
  'Industry Updates',
  'Job Market',
  'Technology',
  'Education',
  'Government Policy',
  'Research & Development',
  'Sustainability',
];

const priorities = [
  { label: 'Low', value: 'low', color: '#6b7280' },
  { label: 'Medium', value: 'medium', color: '#f59e0b' },
  { label: 'High', value: 'high', color: '#ef4444' },
  { label: 'Breaking', value: 'breaking', color: '#dc2626' },
];

export default function AddNewsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const isEditing = route.params?.newsItem ? true : false;
  const existingNews = route.params?.newsItem || {};

  const [formData, setFormData] = useState({
    title: existingNews.title || '',
    content: existingNews.content || '',
    snippet: existingNews.snippet || '',
    category: existingNews.category || categories[0],
    source: existingNews.source || '',
    author: existingNews.author || '',
    tags: existingNews.tags?.join(', ') || '',
    priority: existingNews.priority || 'medium',
    status: existingNews.status || 'draft',
    imageUrl: existingNews.imageUrl || '',
  });

  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showPriorityPicker, setShowPriorityPicker] = useState(false);
  const [isPublished, setIsPublished] = useState(existingNews.status === 'published');
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (saveAsDraft = false) => {
    if (!formData.title.trim()) {
      Alert.alert('Error', 'Please enter a title for the news article');
      return;
    }

    if (!formData.content.trim()) {
      Alert.alert('Error', 'Please enter content for the news article');
      return;
    }

    setIsSaving(true);

    try {
      const newsData = {
        ...formData,
        id: existingNews.id || Date.now().toString(),
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        status: saveAsDraft ? 'draft' : (isPublished ? 'published' : 'draft'),
        publishDate: isPublished ? new Date() : existingNews.publishDate,
        lastModified: new Date(),
        readTime: Math.ceil(formData.content.split(' ').length / 200), // Estimate reading time
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      Alert.alert(
        'Success',
        `News article ${isEditing ? 'updated' : 'created'} successfully!`,
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to save news article. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    navigation.navigate('NewsDetail', {
      newsItem: {
        ...formData,
        id: 'preview',
        publishDate: new Date(),
        readTime: Math.ceil(formData.content.split(' ').length / 200),
      },
      isPreview: true,
    });
  };

  const renderCategoryPicker = () => {
    if (!showCategoryPicker) return null;

    return (
      <View style={styles.pickerOverlay}>
        <View style={styles.pickerContainer}>
          <View style={styles.pickerHeader}>
            <Text style={styles.pickerTitle}>Select Category</Text>
            <TouchableOpacity onPress={() => setShowCategoryPicker(false)}>
              <MaterialIcons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.pickerContent}>
            {categories.map((category, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.pickerItem,
                  formData.category === category && styles.selectedPickerItem,
                ]}
                onPress={() => {
                  handleInputChange('category', category);
                  setShowCategoryPicker(false);
                }}
              >
                <Text
                  style={[
                    styles.pickerItemText,
                    formData.category === category && styles.selectedPickerItemText,
                  ]}
                >
                  {category}
                </Text>
                {formData.category === category && (
                  <MaterialIcons name="check" size={20} color="#0e4e96" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    );
  };

  const renderPriorityPicker = () => {
    if (!showPriorityPicker) return null;

    return (
      <View style={styles.pickerOverlay}>
        <View style={styles.pickerContainer}>
          <View style={styles.pickerHeader}>
            <Text style={styles.pickerTitle}>Select Priority</Text>
            <TouchableOpacity onPress={() => setShowPriorityPicker(false)}>
              <MaterialIcons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.pickerContent}>
            {priorities.map((priority, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.pickerItem,
                  formData.priority === priority.value && styles.selectedPickerItem,
                ]}
                onPress={() => {
                  handleInputChange('priority', priority.value);
                  setShowPriorityPicker(false);
                }}
              >
                <View style={styles.priorityItem}>
                  <View
                    style={[
                      styles.priorityIndicator,
                      { backgroundColor: priority.color },
                    ]}
                  />
                  <Text
                    style={[
                      styles.pickerItemText,
                      formData.priority === priority.value && styles.selectedPickerItemText,
                    ]}
                  >
                    {priority.label}
                  </Text>
                </View>
                {formData.priority === priority.value && (
                  <MaterialIcons name="check" size={20} color="#0e4e96" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialIcons name="arrow-back" size={24} color="#0e4e96" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {isEditing ? 'Edit News' : 'Add News'}
          </Text>
          <TouchableOpacity
            style={styles.previewButton}
            onPress={handlePreview}
            disabled={!formData.title || !formData.content}
          >
            <MaterialCommunityIcons
              name="eye-outline"
              size={24}
              color={formData.title && formData.content ? "#0e4e96" : "#ccc"}
            />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Title Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Title *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.title}
              onChangeText={(value) => handleInputChange('title', value)}
              placeholder="Enter news title..."
              multiline
              maxLength={200}
            />
            <Text style={styles.characterCount}>
              {formData.title.length}/200
            </Text>
          </View>

          {/* Snippet Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Snippet</Text>
            <TextInput
              style={[styles.textInput, styles.snippetInput]}
              value={formData.snippet}
              onChangeText={(value) => handleInputChange('snippet', value)}
              placeholder="Brief summary for preview..."
              multiline
              maxLength={300}
            />
            <Text style={styles.characterCount}>
              {formData.snippet.length}/300
            </Text>
          </View>

          {/* Content Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Content *</Text>
            <TextInput
              style={[styles.textInput, styles.contentInput]}
              value={formData.content}
              onChangeText={(value) => handleInputChange('content', value)}
              placeholder="Write your news content here..."
              multiline
              textAlignVertical="top"
            />
            <Text style={styles.characterCount}>
              {formData.content.length} characters
            </Text>
          </View>

          {/* Category and Priority Row */}
          <View style={styles.rowInputs}>
            <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
              <Text style={styles.inputLabel}>Category</Text>
              <TouchableOpacity
                style={styles.selectInput}
                onPress={() => setShowCategoryPicker(true)}
              >
                <Text style={styles.selectInputText}>{formData.category}</Text>
                <MaterialIcons name="keyboard-arrow-down" size={24} color="#666" />
              </TouchableOpacity>
            </View>

            <View style={[styles.inputGroup, { flex: 1, marginLeft: 10 }]}>
              <Text style={styles.inputLabel}>Priority</Text>
              <TouchableOpacity
                style={styles.selectInput}
                onPress={() => setShowPriorityPicker(true)}
              >
                <View style={styles.priorityDisplay}>
                  <View
                    style={[
                      styles.priorityIndicator,
                      {
                        backgroundColor: priorities.find(p => p.value === formData.priority)?.color || '#6b7280',
                      },
                    ]}
                  />
                  <Text style={styles.selectInputText}>
                    {priorities.find(p => p.value === formData.priority)?.label || 'Medium'}
                  </Text>
                </View>
                <MaterialIcons name="keyboard-arrow-down" size={24} color="#666" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Source and Author Row */}
          <View style={styles.rowInputs}>
            <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
              <Text style={styles.inputLabel}>Source</Text>
              <TextInput
                style={styles.textInput}
                value={formData.source}
                onChangeText={(value) => handleInputChange('source', value)}
                placeholder="News source..."
              />
            </View>

            <View style={[styles.inputGroup, { flex: 1, marginLeft: 10 }]}>
              <Text style={styles.inputLabel}>Author</Text>
              <TextInput
                style={styles.textInput}
                value={formData.author}
                onChangeText={(value) => handleInputChange('author', value)}
                placeholder="Author name..."
              />
            </View>
          </View>

          {/* Tags Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Tags</Text>
            <TextInput
              style={styles.textInput}
              value={formData.tags}
              onChangeText={(value) => handleInputChange('tags', value)}
              placeholder="Enter tags separated by commas..."
            />
            <Text style={styles.helperText}>
              Separate multiple tags with commas
            </Text>
          </View>

          {/* Image URL Input */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Image URL</Text>
            <TextInput
              style={styles.textInput}
              value={formData.imageUrl}
              onChangeText={(value) => handleInputChange('imageUrl', value)}
              placeholder="https://example.com/image.jpg"
              keyboardType="url"
            />
          </View>

          {/* Publish Toggle */}
          <View style={styles.inputGroup}>
            <View style={styles.toggleRow}>
              <View>
                <Text style={styles.inputLabel}>Publish Immediately</Text>
                <Text style={styles.helperText}>
                  {isPublished ? 'Article will be published' : 'Save as draft'}
                </Text>
              </View>
              <Switch
                value={isPublished}
                onValueChange={setIsPublished}
                trackColor={{ false: '#e5e7eb', true: '#0e4e96' }}
                thumbColor={isPublished ? '#fff' : '#f4f3f4'}
              />
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.actionButton, styles.draftButton]}
              onPress={() => handleSave(true)}
              disabled={isSaving}
            >
              <MaterialCommunityIcons
                name="content-save-outline"
                size={20}
                color="#666"
              />
              <Text style={styles.draftButtonText}>Save Draft</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.actionButton, styles.publishButton]}
              onPress={() => handleSave(false)}
              disabled={isSaving || !formData.title || !formData.content}
            >
              {isSaving ? (
                <MaterialCommunityIcons
                  name="loading"
                  size={20}
                  color="#fff"
                />
              ) : (
                <MaterialCommunityIcons
                  name={isPublished ? "publish" : "content-save"}
                  size={20}
                  color="#fff"
                />
              )}
              <Text style={styles.publishButtonText}>
                {isEditing ? 'Update' : (isPublished ? 'Publish' : 'Save')}
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>

        {/* Pickers */}
        {renderCategoryPicker()}
        {renderPriorityPicker()}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
  },
  previewButton: {
    padding: 5,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    color: '#1f2937',
  },
  snippetInput: {
    minHeight: 80,
  },
  contentInput: {
    minHeight: 150,
  },
  characterCount: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'right',
    marginTop: 5,
  },
  helperText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 5,
  },
  rowInputs: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  selectInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
  },
  selectInputText: {
    fontSize: 16,
    color: '#1f2937',
  },
  priorityDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionButtons: {
    flexDirection: 'row',
    marginTop: 20,
    marginBottom: 40,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
    borderRadius: 8,
    marginHorizontal: 5,
  },
  draftButton: {
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  draftButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
    marginLeft: 8,
  },
  publishButton: {
    backgroundColor: '#0e4e96',
  },
  publishButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 8,
  },
  pickerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  pickerContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    margin: 20,
    maxHeight: '70%',
    width: '90%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pickerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
  },
  pickerContent: {
    maxHeight: 300,
  },
  pickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  selectedPickerItem: {
    backgroundColor: '#f0f9ff',
  },
  pickerItemText: {
    fontSize: 16,
    color: '#1f2937',
  },
  selectedPickerItemText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  priorityItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

